function createArohaPackaging(config, io = {}) {
  const parents = new Set(config.parentVariantIds.map(String));
  const supported = ({NZ:'NZD',AU:'AUD'})[config.country] === config.currency && Object.values(config.upgrades).every(item => item.available && Number.isSafeInteger(item.price) && item.price > 0 && item.price === item.expectedPrice && item.tracked === false);
  const themeAllowed = Number(config.stagingThemeId) === 0 || Number(config.stagingThemeId) === Number(config.themeId);
  const ready = config.enabled === true && supported && themeAllowed;
  const send = io.fetch || fetch;
  const states = new WeakMap();
  const market = io.market || (typeof window !== 'undefined' ? window.ArohaMarket : null);
  const fail = () => new Error(config.messages.review);
  const variant = item => String(item.variant_id ?? item.id);
  const quantity = value => {
    const result = Number(value);
    if (!Number.isSafeInteger(result) || result < 0) throw fail();
    return result;
  };
  function buildAddPayload(formData, choice) {
    if (!ready || choice === 'standard') return null;
    const parentId = String(formData.get('id'));
    const upgrade = config.upgrades[choice];
    const amount = quantity(formData.get('quantity') || 1);
    if (!parents.has(parentId) || !upgrade || amount === 0 || formData.get('selling_plan')) throw fail();
    const properties = {};
    for (const [name, value] of formData.entries()) {
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      if (match) properties[match[1]] = value;
    }
    const payload = { items: [
      { id: Number(parentId), quantity: amount, properties },
      { id: Number(upgrade.variantId), quantity: amount, properties: { 'Packaging rule': "Each packaging item receiving the eligible MDF-set credit replaces one included open MDF box. Full-price packaging is additional." } }
    ] };
    for (const name of ['sections', 'sections_url']) if (formData.has(name)) payload[name] = formData.get(name);
    return payload;
  }
  async function fetchAdd(form, formData, originalConfig, url) {
    const field = form.querySelector('[data-aroha-packaging-choice]');
    if (!field || field.value === 'standard') return send(url, originalConfig);
    if (!ready || states.get(form) || !market?.isCurrent(config)) throw fail();
    states.set(form, 'checking');
    let started = false;
    try {
      const upgrade = config.upgrades[field.value], data = await market.read();
      const product = data.products?.[upgrade.handle];
      const native = market.variant(data, upgrade.handle, product?.id, upgrade.variantId, upgrade.price);
      if (!native.available || native.sku !== (field.value === 'stand' ? 'AA-MDF-UPGRADE-STAND' : 'AA-MDF-UPGRADE-CLOSEDBOX')) throw fail();
      const payload = buildAddPayload(formData, field.value), parentId = String(formData.get('id'));
      const marker = io.id?.() || globalThis.crypto.randomUUID();
      for (const item of payload.items) item.properties = {...item.properties, _aa_packaging_add: marker};
      const before = await send(config.root.replace(/\/?$/, '/') + 'cart.js', {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}});
      if (!before.ok || (await before.json()).currency !== config.currency || !market.isCurrent(config)) throw fail();
      started = true;
      let response, writeError;
      try { response = await send(url, {...originalConfig, headers:{...originalConfig.headers, 'Content-Type':'application/json'}, body:JSON.stringify(payload)}); } catch (error) { writeError = error; }
      const after = await send(config.root.replace(/\/?$/, '/') + 'cart.js', {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}});
      if (!after.ok) throw fail();
      const cart = await after.json(), recorded = (cart.items || []).filter(item => item.properties?._aa_packaging_add === marker);
      const count = id => recorded.filter(item => variant(item) === String(id)).reduce((sum,item) => sum + item.quantity, 0);
      if (writeError || !response?.ok || cart.currency !== config.currency || !market.isCurrent(config) || count(parentId) !== payload.items[0].quantity || count(upgrade.variantId) !== payload.items[1].quantity || recorded.some(item => variant(item) === String(upgrade.variantId) && item.original_price !== upgrade.price)) throw fail();
      states.delete(form);
      return response;
    } catch (error) {
      if (started) states.set(form, 'uncertain'); else states.delete(form);
      throw fail();
    }
  }
  function normalizeAddResponse(response, parentId) {
    if (!ready || response.status || !Array.isArray(response.items)) return response;
    const parent = response.items.find(item => variant(item) === String(parentId));
    return parent ? { ...response, ...parent } : response;
  }
  return { ready, config, buildAddPayload, fetchAdd, normalizeAddResponse };
}
if (typeof module === 'object' && module.exports) module.exports = createArohaPackaging;
if (typeof document !== 'undefined') {
  const source = document.getElementById('ArohaPackagingConfig');
  if (source) {
    const api = createArohaPackaging(JSON.parse(source.textContent));
    window.ArohaPackaging = api;
    if (api.ready) {
      const choices = new Map();
      const decorate = () => {
        document.querySelectorAll('[data-aroha-packaging-picker]').forEach(fieldset => {
          fieldset.hidden = false;
          fieldset.disabled = false;
          const select = fieldset.querySelector('select');
          if (!fieldset.dataset.ready) {
            select.value = choices.get(fieldset.dataset.productId) || 'standard';
            for (const [key, item] of Object.entries(api.config.upgrades)) {
              const option = Array.from(select.options).find(option => option.value === key);
              if (option) option.textContent = item.label + ' — ' + new Intl.NumberFormat('en-NZ', {style:'currency',currency:api.config.currency}).format(item.price/100) + ' before set credit';
            }
            const help = fieldset.querySelector('.caption');
            if (help) help.textContent = 'Eligible MDF-set credit is applied automatically in your cart. Discounted packaging replaces the included open MDF box. Additional packaging is full price.';
            fieldset.dataset.ready = 'true';
          }
        });
      };
      document.addEventListener('change', event => {
        if (event.target.matches('[data-aroha-packaging-choice]')) choices.set(event.target.closest('fieldset').dataset.productId, event.target.value);
      });
      let scheduled = false;
      new MutationObserver(() => {
        if (!scheduled) {
          scheduled = true;
          queueMicrotask(() => { scheduled = false; decorate(); });
        }
      }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
      decorate();
    }
  }
}
